/**
 * Manager Notes Page
 *
 * Manager view for managing private notes about employees.
 * Story 6-2: Privé Notities (Manager)
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useEmployees } from '../employees/useEmployees';
import {
  useManagerNotes,
  useCreateManagerNote,
  useUpdateManagerNote,
  useDeleteManagerNote,
} from './useNotes';
import type { NoteType, ManagerNote } from '../../lib/notesApi';

const NOTE_TYPE_LABELS: Record<NoteType, { label: string; color: string }> = {
  general: { label: 'Algemeen', color: 'bg-gray-100 text-gray-700' },
  performance: { label: 'Prestatie', color: 'bg-blue-100 text-blue-700' },
  meeting: { label: 'Gesprek', color: 'bg-purple-100 text-purple-700' },
  development: { label: 'Ontwikkeling', color: 'bg-green-100 text-green-700' },
};

export function ManagerNotesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEmployeeId = searchParams.get('employeeId')
    ? parseInt(searchParams.get('employeeId')!)
    : undefined;

  const { data: employees } = useEmployees();
  const { data: notesData, isLoading } = useManagerNotes(selectedEmployeeId);
  const createNote = useCreateManagerNote();
  const updateNote = useUpdateManagerNote();
  const deleteNote = useDeleteManagerNote();

  const [showNewForm, setShowNewForm] = useState(false);
  const [editingNote, setEditingNote] = useState<ManagerNote | null>(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    type: 'general' as NoteType,
    title: '',
    content: '',
    isPrivate: true,
  });

  const notes = notesData?.data || [];

  const handleCreate = async () => {
    if (!formData.employeeId || !formData.content) return;

    await createNote.mutateAsync({
      employeeId: parseInt(formData.employeeId),
      type: formData.type,
      title: formData.title || undefined,
      content: formData.content,
      isPrivate: formData.isPrivate,
    });

    setShowNewForm(false);
    setFormData({
      employeeId: '',
      type: 'general',
      title: '',
      content: '',
      isPrivate: true,
    });
  };

  const handleUpdate = async () => {
    if (!editingNote) return;

    await updateNote.mutateAsync({
      id: editingNote.id,
      input: {
        type: formData.type,
        title: formData.title || undefined,
        content: formData.content,
        isPrivate: formData.isPrivate,
      },
    });

    setEditingNote(null);
  };

  const handleDelete = async (id: number) => {
    if (confirm('Weet je zeker dat je deze notitie wilt verwijderen?')) {
      await deleteNote.mutateAsync(id);
    }
  };

  const openEditForm = (note: ManagerNote) => {
    setEditingNote(note);
    setFormData({
      employeeId: note.employeeId.toString(),
      type: note.type,
      title: note.title || '',
      content: note.content,
      isPrivate: note.isPrivate,
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Notities laden...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="manager-notes-page" className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mijn Notities</h1>
            <p className="mt-1 text-sm text-gray-500">
              Privé notities over teamleden. Alleen zichtbaar voor jou.
            </p>
          </div>
          <button
            data-testid="add-note-button"
            onClick={() => setShowNewForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuwe Notitie
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter op medewerker
            </label>
            <select
              value={selectedEmployeeId || ''}
              onChange={(e) => {
                if (e.target.value) {
                  setSearchParams({ employeeId: e.target.value });
                } else {
                  setSearchParams({});
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Alle medewerkers</option>
              {employees?.data?.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Notes List */}
        {notes.length === 0 ? (
          <div data-testid="notes-list" className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Geen notities</h3>
            <p className="mt-1 text-sm text-gray-500">
              Maak een nieuwe notitie om te beginnen.
            </p>
          </div>
        ) : (
          <div data-testid="notes-list" className="space-y-4">
            {notes.map((note) => {
              const employee = employees?.data?.find((e) => e.id === note.employeeId);
              return (
                <div
                  key={note.id}
                  data-testid="note-item"
                  className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            NOTE_TYPE_LABELS[note.type].color
                          }`}
                        >
                          {NOTE_TYPE_LABELS[note.type].label}
                        </span>
                        {note.title && (
                          <h3 className="font-medium text-gray-900">{note.title}</h3>
                        )}
                        {note.isPrivate ? (
                          <span data-testid="private-indicator" className="flex items-center gap-1 text-xs text-gray-400">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Privé
                          </span>
                        ) : (
                          <span data-testid="shared-indicator" className="flex items-center gap-1 text-xs text-blue-500">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                            </svg>
                            Gedeeld
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditForm(note)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <p className="mt-2 text-gray-700 whitespace-pre-wrap">{note.content}</p>

                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                      <span>Over: {employee?.name || `Medewerker #${note.employeeId}`}</span>
                      <span>|</span>
                      <span>
                        {new Date(note.createdAt).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* New/Edit Note Modal */}
        {(showNewForm || editingNote) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {editingNote ? 'Notitie Bewerken' : 'Nieuwe Notitie'}
              </h3>

              <div className="space-y-4">
                {!editingNote && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Medewerker *
                    </label>
                    <select
                      value={formData.employeeId}
                      onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecteer medewerker</option>
                      {employees?.data?.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      data-testid="note-type-select"
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value as NoteType })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Object.entries(NOTE_TYPE_LABELS).map(([type, { label }]) => (
                        <option key={type} value={type}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.isPrivate}
                        onChange={(e) =>
                          setFormData({ ...formData, isPrivate: e.target.checked })
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Privé notitie</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titel (optioneel)
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Korte titel voor de notitie"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notitie *
                  </label>
                  <textarea
                    data-testid="note-content"
                    rows={5}
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Schrijf je notitie hier..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowNewForm(false);
                    setEditingNote(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  data-testid="save-note-button"
                  onClick={editingNote ? handleUpdate : handleCreate}
                  disabled={
                    (!editingNote && !formData.employeeId) ||
                    !formData.content ||
                    createNote.isPending ||
                    updateNote.isPending
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createNote.isPending || updateNote.isPending
                    ? 'Opslaan...'
                    : editingNote
                    ? 'Bijwerken'
                    : 'Opslaan'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
